/**
 * Build an Error from a failed `fetch` Response with a message worth reading.
 *
 * The previous wrappers used `response.statusText`, which HTTP/2 ALWAYS leaves
 * blank — so a real 404/500 surfaced in the UI as a bare "API request failed:".
 * This reads the status code (always present) plus the response body (NestJS
 * `message`/`error`, else raw text), and stamps `status`/`statusCode` for the
 * downstream auth-refresh + 401/403 handling.
 */
export async function buildApiError(
  response: Response
): Promise<Error & { status: number; statusCode: number }> {
  let detail = ''
  try {
    const text = await response.text()
    if (text) {
      try {
        const json = JSON.parse(text)
        // NestJS error shape: { statusCode, error, message }; message may be a
        // string or a string[] (validation errors).
        const msg = json?.message ?? json?.error
        detail = Array.isArray(msg)
          ? msg.join('; ')
          : typeof msg === 'string'
            ? msg
            : text.slice(0, 300)
      } catch {
        detail = text.slice(0, 300)
      }
    }
  } catch {
    // Body unreadable/already consumed — the status code alone still beats blank.
  }

  const label = detail || response.statusText || 'request failed'
  const error = new Error(`API request failed (${response.status}): ${label}`) as Error & {
    status: number
    statusCode: number
  }
  error.status = response.status
  error.statusCode = response.status
  return error
}
